import { useCallback, useEffect, useState } from 'react';
import { useTRPCRequest } from '../../../hooks';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { toast } from 'react-toastify';
import { Style } from '../types';

export type StyleFilterType = 'all' | 'base' | 'partner';

export interface StylesFilterOptions {
  search?: string;
  filterType?: StyleFilterType;
  partnerId?: string;
}

export interface StyleWithPartner extends Style {
  partner_name?: string | null;
}

export function useStyles(options: StylesFilterOptions = {}) {
  const { handleTRPCRequest } = useTRPCRequest();
  const { search, filterType = 'all', partnerId } = options;

  const [styles, setStyles] = useState<StyleWithPartner[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchStyles = useCallback(
    async (fetchOptions?: StylesFilterOptions) => {
      setIsLoading(true);
      const requestBody: Record<string, unknown> = {};

      const opts = fetchOptions || options;

      if (opts.search) {
        requestBody.keyword = opts.search;
      }

      // Filter type: 'base' = only base styles, 'partner' = only partner styles, 'all' = everything
      if (opts.filterType === 'base') {
        requestBody.include_partner_styles = false;
      } else if (opts.filterType === 'partner') {
        requestBody.partner_styles_only = true;
      } else {
        requestBody.include_partner_styles = true;
      }

      // Filter by specific partner
      if (opts.partnerId) {
        requestBody.partner_id = opts.partnerId;
      }

      const { res: styles, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.list,
        resourceType: TRPCResourceEnum.styles,
        requestBody
      });

      if (error) {
        toast.error('Error fetching styles');
      }
      setStyles(styles as StyleWithPartner[]);
      setIsLoading(false);
    },
    [handleTRPCRequest, options]
  );

  useEffect(() => {
    fetchStyles({ search, filterType, partnerId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterType, partnerId]);

  return { styles, isLoading, refetch: fetchStyles };
}
